<template>
	<div class="video-manager">
		<main class="video-manager-wrapper">
			<section class="video-manager-information">
				<aside class="information">
					<p class="video-manager-text">VIDEO REVIEW MANAGER</p>
					<h1 class="video-manager-header">
						Request and Publish Borrower Video Reviews To Mortgage Matchup and
						Google Business Profile<sup>TM</sup>
					</h1>
					<p class="video-manager-description">
						Brand 360’s Video Reviews feature automatically invites your past
						borrowers to leave a video testimonial about their mortgage
						experience and makes it easy to share them on your Mortgage Matchup
						and Google Business Profile.
					</p>
					<div class="list">
						<p class="video-manager-description">
							Activating your account will allow you to:
						</p>
						<ul>
							<li class="video-manager-list-item">
								<div class="icon-wrapper">
									<font-awesome-icon :icon="['fal', 'envelope']" />
								</div>
								Automatically send text messages to clients who have closed a
								loan with you asking for a review
							</li>
							<li class="video-manager-list-item">
								<div class="icon-wrapper">
									<font-awesome-icon :icon="['fal', 'video']" />
								</div>
								Preview and manage video reviews before publishing
							</li>
							<li class="video-manager-list-item">
								<div class="icon-wrapper">
									<font-awesome-icon :icon="['fal', 'arrow-to-top']" />
								</div>
								Publish video reviews to Mortgage Matchup and your Google
								Business Profile
							</li>
						</ul>
					</div>
				</aside>
				<div class="image-container">
					<img
						src="https://res.cloudinary.com/uwm/image/upload/f_auto,q_auto/v1/video-review-assets/SocialV-Screenshot1"
						alt="two phones with video review information displayed"
						class="video-manager-screenshot"
					/>
				</div>
			</section>
			<section class="video-manager-activation">
				<p class="activation-text">
					When you activate your Video Reviews account, all future closed loans
					will automatically be added to your account and a text message will be
					sent to the borrowers on your behalf asking for a video review.
				</p>
				<form class="form">
					<p class="form-text" style="padding: 0">
						Want to start out with more reviews? You have the option to request
						video reviews from all clients who closed within the last 30 days
					</p>

					<div class="input">
						<input
							type="radio"
							id="include-loans"
							name="loan-option"
							value="true"
							v-model="includeLast30Days"
							:disabled="!canCreateSocialVAccount"
							data-testid="include-loans"
						/>
						<label
							for="include-loans"
							class="label"
							:class="{ disabled: !canCreateSocialVAccount }"
							data-testid="include-loans-label"
						>
							Yes, include the last 30 days of closed loans to my account</label
						>
					</div>
					<div class="input">
						<input
							type="radio"
							id="exclude-loans"
							name="loan-option"
							value="false"
							v-model="includeLast30Days"
							:disabled="!canCreateSocialVAccount"
							data-testid="exclude-loans"
						/>
						<label
							for="exclude-loans"
							class="label"
							:class="{ disabled: !canCreateSocialVAccount }"
							data-testid="exclude-loans-label"
						>
							No, I only want future closed loans added to my account
						</label>
					</div>
				</form>
			</section>
			<div class="button-row">
				<div class="message-wrapper">
					<p class="disabled-message" v-if="!canCreateSocialVAccount" data-testid="disabled-message">
						It looks like you don’t have permission to access this feature. Please
						reach out to your Account Executive. 
					</p>
				</div>
				<BaseButton
					color="blue"
					@onClick="activateVideos"
					:disabled="isDisabled"
					:status="buttonStatus"
					width="130px"
					>Activate
					<font-awesome-icon
						class="external-link"
						:icon="['far', 'external-link-alt']"
				/></BaseButton>
			</div>
			<p class="disclaimer">
				<i
					>Brand 360 users are responsible for complying with any and all
					applicable state and federal consumer laws including, but not limited
					to, laws that govern the obtaining and sharing of customer video
					reviews.</i
				>
			</p>
		</main>
	</div>
</template>

<script>
import BaseButton from '@/components/global/BaseButton.vue';
import FormRadioButtons from '@/components/global/FormRadioButtons.vue';
import { mapActions, mapState, mapGetters } from 'vuex';

export default {
	name: 'VideoReviewActivation',
	components: {
		BaseButton,
		FormRadioButtons,
	},
	data() {
		return {
			includeLast30Days: null,
			buttonStatus: null,
		};
	},
	computed: {
		...mapState({
			DBA: (state) => state.companyProfile.dba,
		}),
		...mapGetters(['canCreateSocialVAccount']),
		isDisabled() {
			return this.includeLast30Days === null;
		},
		includeLast30DaysBool() {
			return this.includeLast30Days === 'true';
		},
	},
	methods: {
		...mapActions(['getADFSLink']),
		async activateVideos() {
			try {
				this.buttonStatus = 'waiting';
				const accountActivationData = {
					exportRecentLoanLeadData: this.includeLast30DaysBool,
					companyName: this.DBA,
				};
				await this.getADFSLink(accountActivationData);
			} catch (error) {
			} finally {
				this.$router.push({ name: 'Dashboard' });
			}
		},
	},
};
</script>

<style scoped lang="scss">
.video-manager {
	height: 100%;
}

.video-manager-wrapper {
	width: 53%;
	max-width: 1272px;
	margin: auto;
	background-color: white;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	padding: 50px 0;
}

.video-manager-information {
	display: flex;
	flex-wrap: nowrap;
	justify-content: space-between;
	align-items: center;
	padding-left: 100px;
	gap: 25px;
}

.information {
	flex: 1 1 50%;
}

.video-manager-text {
	font-size: 12px;
	color: #08568b;
}

.video-manager-header {
	margin: 10px 0 20px 0;
	line-height: 40px;

	sup {
		font-size: 8px;
	}
}

.video-manager-description {
	line-height: 24px;
}

.list {
	margin-top: 20px;
}

.video-manager-list-item {
	margin: 28px 0;
	font-size: 14px;
	display: flex;
	align-items: center;
	line-height: 24px;
}

.icon-wrapper {
	margin-right: 10px;
	background-color: #00807b14;
	padding: 4px;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	color: #00a39d;
	text-align: center;
}

.image-container {
	background-color: #00807b1f;
	margin-left: 100px;
	padding: 35px 0;
}

.video-manager-screenshot {
	flex: 1 1 707px;
	min-width: 0;
	max-width: 100%;
	height: auto;
	transform: translate(-25px);
}

.video-manager-activation {
	margin: 30px 100px;
	padding: 30px 0;
	border-top: 1px solid #0000001f;
	border-bottom: 1px solid #0000001f;
}

.activation-text {
	font-size: 18px;
	line-height: 28px;
}

.form {
	margin: 20px 0;
	padding: 10px;
	background-color: #f7f7f7;
	border-radius: 8px;
}

.form-text {
	margin: 16px 0;
	font-size: 18px;
	line-height: 28px;
}

.input {
	margin: 16px 0;

	input:disabled {
		color: $b360-color--gray__darker-80;
		cursor: not-allowed;
	}

	.label.disabled {
		color: $b360-color--gray__darker-80;
		cursor: not-allowed;
	}
}

.button-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin: 30px 100px;
	gap: 20px;
}

.disabled-message {
	font-size: 16px;
	padding-top: 16px;
	margin: 0;
	color: #08568b;
}

.external-link {
	margin-left: 5px;
}

.disclaimer {
	margin: 20px auto;
	font-size: 12px;
	max-width: 708px;
	text-align: center;
	line-height: 2;
}

@media (max-width: 2300px) {
	.video-manager-wrapper {
		width: 70%;
	}
}
@media (max-width: 1900px) {
	.video-manager-wrapper {
		width: 80%;
	}
}
@media (max-width: 1600px) {
	.video-manager-wrapper {
		width: 90%;
	}
}
@media (max-width: 1430px) {
	.video-manager-wrapper {
		width: 95%;
	}
}
@media (max-width: 1240px) {
	.video-manager-wrapper {
		width: 95%;
		padding: unset;
	}
	.information {
		padding: 0 20px;
	}
	.video-manager-information {
		flex-direction: column-reverse;
		padding-left: 0px;
	}
	.image-container {
		box-sizing: border-box;
		margin-left: 0;
		width: 100%;
		padding: 35px;
	}

	.video-manager-screenshot {
		transform: unset;
		display: block;
		margin: auto;
	}
	.video-manager-activation {
		margin: 30px 20px;
	}
	.button-row {
		margin: 30px;
	}
}
</style>
